import { ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import {
  PJ_NF_STATUS_CLASS,
  PJ_NF_STATUS_LABELS,
} from '@/components/PjNf/pjNfStatus'
import type { PjNfMensalRow } from '@/services/pjNfMensalService'
import { formatCnpj } from '@/components/PjAgrada/pjAgradaFormat'

type Props = {
  rows: PjNfMensalRow[]
  loading: boolean
  onSelect: (row: PjNfMensalRow) => void
}

function formatMoney(value: number | null | undefined) {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR')
}

export function FinancePagamentosTable({ rows, loading, onSelect }: Props) {
  return (
    <GestorListTableShell
      loading={loading}
      loadingMessage='Carregando NFs do mês…'
      empty={!loading && rows.length === 0}
      emptyTitle='Nenhum prestador nesta competência'
      emptyHint='Ajuste o mês ou os filtros aplicados.'
    >
      <table className='w-full'>
        <thead>
          <tr className='border-b border-[var(--glass-border)]'>
            {['Prestador', 'Setor', 'Valor esperado', 'Status', 'Enviado em', ''].map((h) => (
              <th
                key={h || 'action'}
                className={GESTOR_TABLE_TH_CLASS}
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={GESTOR_TABLE_TR_CLASS}
              onClick={() => onSelect(row)}
            >
              <td className='px-4 py-3'>
                <div className='flex items-center gap-3'>
                  <GestorTableAvatar name={row.prestador?.nomeCompleto ?? 'PJ'} />
                  <div className='min-w-0'>
                    <p
                      className='truncate font-medium text-[var(--font-color-body)]'
                      style={{ fontSize: 'var(--font-size-sm)' }}
                    >
                      {row.prestador?.nomeCompleto ?? '—'}
                    </p>
                    {row.prestador?.cnpj && (
                      <p className='mt-0.5 font-mono text-[11px] text-[var(--font-color-muted)]'>
                        {formatCnpj(row.prestador.cnpj)}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className='px-4 py-3 text-sm text-[var(--font-color-muted)]'>
                {row.prestador?.setor ?? row.prestador?.areaAtuacao ?? '—'}
              </td>
              <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>
                {formatMoney(row.valorEsperado ?? row.prestador?.remuneracaoMensal)}
              </td>
              <td className='px-4 py-3'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span
                    className={cn(
                      'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                      PJ_NF_STATUS_CLASS[row.status],
                    )}
                  >
                    {PJ_NF_STATUS_LABELS[row.status]}
                  </span>
                  {row.enviadoComRessalvas && (
                    <span className='inline-flex rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700'>
                      Ressalvas
                    </span>
                  )}
                </div>
              </td>
              <td className='px-4 py-3 text-sm text-[var(--font-color-muted)]'>
                {formatDate(row.enviadoEm)}
              </td>
              <td className='px-4 py-3'>
                <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GestorListTableShell>
  )
}
